import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Truck, Download, Eye, FileText, Loader2, CheckCircle2 } from 'lucide-react';
import { StatusBadge } from './StatusBadge';

const SVE_API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

// Compact date formatter - uses short format to prevent overflow
// Parses YYYY-MM-DD as local date to avoid timezone shift issues
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    // Parse date string manually to avoid timezone issues
    // Date strings like "2025-12-01" should display as Dec 1, not Nov 30
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length >= 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
      const day = parseInt(parts[2], 10);
      const date = new Date(year, month, day);
      const dayNum = date.getDate();
      const monthStr = date.toLocaleDateString('en-IN', { month: 'short' });
      return `${dayNum} ${monthStr} ${year}`;
    }
    // Fallback for other formats
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-IN', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return '-';
  }
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return '₹' + Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

// Format rate to show exact decimal value without rounding
const formatRate = (rate) => {
  if (!rate && rate !== 0) return '-';
  // Show up to 4 decimal places, removing trailing zeros
  const num = Number(rate);
  if (isNaN(num)) return '-';
  return '₹' + num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
};

const calculateDaysRemaining = (expectedDate) => {
  if (!expectedDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Parse date string manually to avoid timezone issues
  const parts = expectedDate.split('T')[0].split('-');
  let expected;
  if (parts.length >= 3) {
    expected = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  } else {
    expected = new Date(expectedDate);
  }
  expected.setHours(0, 0, 0, 0);
  return Math.floor((expected - today) / (1000 * 60 * 60 * 24));
};

// Section Header with completion indicator
const SectionHeader = ({ title, isComplete, textColor = 'text-slate-800' }) => (
  <div className={`${isComplete ? 'bg-slate-100' : 'bg-slate-50'} px-4 py-2 border-b flex items-center justify-between`}>
    <h4 className={`font-semibold ${isComplete ? 'text-slate-700' : 'text-slate-600'} text-sm`}>{title}</h4>
    {isComplete && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
  </div>
);

// Field display component
const Field = ({ label, value, className = '' }) => (
  <div className={`min-w-0 overflow-hidden ${className}`}>
    <Label className="text-xs text-slate-500">{label}</Label>
    <p className="font-medium truncate">{value || '-'}</p>
  </div>
);

export function ViewLoadDialog({ 
  open, 
  onClose, 
  load, 
  onEdit,
  onEditSection,
  onGetR2File, 
  attachmentLoading = {},
  isSuperUser = false
}) {
  if (!load) return null;

  // Prefer the actual uploaded filename (preserves real extension for JPEG/PNG/etc.);
  // fall back to `${invoiceNumber}.pdf` for generated invoices which are always PDFs.
  const pickDownloadName = (uploadedFilename, invoiceNumber, fallback) => {
    if (uploadedFilename) return uploadedFilename;
    const base = invoiceNumber || fallback;
    return `${base}.pdf`;
  };

  // Add data-testid to key elements
  const loadClientName = load.client_name;
  const loadInvoiceNumber = load.generated_invoice_number || load.generated_invoice_number_1;

  // Legacy base64 download
  const handleDownload = (file, filename) => {
    const link = document.createElement('a');
    link.href = `data:application/octet-stream;base64,${file}`;
    link.download = filename;
    link.click();
  };

  // Check if this is a BALAJI or SVE client
  // Adhoc loads carry their real client_name but flag their style via invoice_style,
  // so also treat client_type='adhoc' + invoice_style match as Balaji/SVE for the
  // purpose of showing the correct downstream sections (Updated Invoice /
  // Weight Adjustment).
  const isBalaji = load.client_name === 'BALAJI CASTINGS PVT LTD'
                   || (load.client_type === 'adhoc' && load.invoice_style === 'balaji');
  const isSve = load.client_name === 'SVE CASTINGS PVT LTD'
                   || (load.client_type === 'adhoc' && load.invoice_style === 'sve');
  const isSveAdditionDeduction = isSve && load.sve_load_type === 'Addition/Deduction';

  // Check section completion status
  const isBasicInfoComplete = !!(load.material_from && load.date && load.purchase_order && 
                                  load.vehicle_number && load.driver_name && load.driver_contact && 
                                  load.transport_company && load.client_name && load.date_sent);
  
  // Check for two-invoice mode
  const isTwoInvoiceMode = Number(load.invoice_count) === 2;
  const isInvoiceComplete = isTwoInvoiceMode
    ? !!(load.product && load.kgs_1 && load.kgs_2 && load.rate && 
         load.generated_invoice_file_1 && load.generated_invoice_file_2)
    : !!(load.product && load.kgs && load.rate && 
         (load.invoice_filename || load.generated_invoice_file) &&
         load.permit_filename);
  
  // Delivery & Status is complete when: delivered + date_received + acceptance (accepted or rejected)
  const isDeliveryComplete = load.delivery_status === 'delivered' && 
                             !!load.date_received && 
                             (load.acceptance_status === 'accepted' || load.acceptance_status === 'rejected');
  
  const isUpdatedInvoiceComplete = isBalaji && load.acceptance_status === 'accepted' && 
                                    !!(load.total_kgs_updated && (load.generated_updated_invoice_file || load.updated_invoice_file_url || load.updated_invoice_filename));
  
  const isWeightAdjustmentComplete = isSve && load.acceptance_status === 'accepted' && 
                                      !!load.weight_status && 
                                      (load.weight_status === 'addition' 
                                        ? !!(load.additional_tons && load.additional_invoice_amount && (load.generated_addition_invoice_file || load.addition_invoice_file_url || load.addition_invoice_filename))
                                        : !!load.deduction_tons);
  
  const isBillingComplete = load.bill_status === 'paid';
  
  const isPaymentComplete = !!load.actual_payment_date;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="view-load-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-slate-600" />
            Load Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Status Badges */}
          <div className={`border rounded-xl overflow-hidden ${isDeliveryComplete ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'}`}>
            <SectionHeader title="Status" isComplete={isSveAdditionDeduction ? isBillingComplete : isDeliveryComplete} />
            <div className="p-3 flex flex-wrap gap-2">
              {!isSveAdditionDeduction && <StatusBadge status={load.delivery_status} type="delivery" />}
              {!isSveAdditionDeduction && <StatusBadge status={load.acceptance_status} type="acceptance" />}
              <StatusBadge status={load.bill_status} type="bill" />
            </div>
          </div>

          {/* SVE Addition/Deduction - Show Summary instead of standard sections */}
          {isSveAdditionDeduction ? (
            <>
              {/* Summary Information */}
              <div className="border rounded-xl overflow-hidden bg-slate-50 border-slate-200">
                <SectionHeader title="Addition/Deduction Summary" isComplete={!!load.summary_generated_invoice_id} />
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Client" value={load.client_name} className="load-client-name" data-testid="load-client-name" />
                    <Field label="Load Type" value={load.sve_load_type} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="From Date" value={formatDate(load.summary_from_date)} />
                    <Field label="To Date" value={formatDate(load.summary_to_date)} />
                  </div>
                  
                  {/* Net Amount Summary */}
                  {(load.summary_net_amount !== undefined && load.summary_net_amount !== null) && (
                    <div className={`p-4 rounded-xl ${Number(load.summary_net_amount) > 0 ? 'bg-green-50 border border-green-200' : Number(load.summary_net_amount) === 0 ? 'bg-slate-50 border border-slate-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-slate-600">Net Amount</Label>
                          <p className={`text-2xl font-bold ${Number(load.summary_net_amount) > 0 ? 'text-green-600' : Number(load.summary_net_amount) === 0 ? 'text-slate-600' : 'text-red-600'}`}>
                            {formatCurrency(Math.abs(Number(load.summary_net_amount)))}
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Net Tons</Label>
                          <p className={`text-2xl font-bold ${Number(load.summary_net_tons || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {Math.abs(Number(load.summary_net_tons || 0)).toFixed(3)} T
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-slate-200 grid grid-cols-2 gap-2 text-xs text-slate-500">
                        <span>Additions: {formatCurrency(load.summary_total_additions || 0)} | {Number(load.summary_total_addition_tons || 0).toFixed(3)}T</span>
                        <span>Deductions: {formatCurrency(load.summary_total_deductions || 0)} | {Number(load.summary_total_deduction_tons || 0).toFixed(3)}T</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Generated Invoice Info */}
                  {load.summary_generated_invoice_id && (
                    <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-5 h-5 text-emerald-600" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-emerald-800">{load.summary_generated_invoice_number || 'Generated Invoice'}</p>
                          <p className="text-xs text-emerald-600">Amount: {formatCurrency(load.summary_invoice_amount)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs h-8"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const response = await fetch(
                                `${SVE_API}/silica-loads/sve-summary-invoice/${load.summary_generated_invoice_id}/view`,
                                { headers: { 'Authorization': `Bearer ${token}` } }
                              );
                              if (!response.ok) throw new Error('Failed to load invoice');
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              window.open(url, '_blank');
                            } catch (error) {
                              console.error('View invoice error:', error);
                            }
                          }}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs h-8"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const response = await fetch(
                                `${SVE_API}/silica-loads/sve-summary-invoice/${load.summary_generated_invoice_id}/download`,
                                {
                                  headers: { 'Authorization': `Bearer ${token}` }
                                }
                              );
                              if (!response.ok) throw new Error('Download failed');
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `${load.summary_generated_invoice_number?.replace(/\//g, '_') || 'SVE_Summary'}.pdf`;
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              a.remove();
                            } catch (error) {
                              console.error('Download error:', error);
                            }
                          }}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
          {/* Basic Information Section */}
          <div className={`border rounded-xl overflow-hidden ${isBasicInfoComplete ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'}`}>
            <SectionHeader title="Basic Information" isComplete={isBasicInfoComplete} />
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Client" value={load.client_name} />
                <Field label="Vehicle Number" value={load.vehicle_number} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Driver Name" value={load.driver_name} />
                <Field label="Driver Contact" value={load.driver_contact} />
                <Field label="Transport Company" value={load.transport_company} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Material From" value={load.material_from} />
                <Field label="Purchase Order" value={load.purchase_order} />
                <Field label="Load Date" value={formatDate(load.date)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Date Sent" value={formatDate(load.date_sent)} />
                <Field label="Date Received" value={formatDate(load.date_received)} />
              </div>
            </div>
          </div>

          {/* Invoice Details Section */}
          <div className={`border rounded-xl overflow-hidden ${isInvoiceComplete ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'}`}>
            <SectionHeader title="Invoice Details" isComplete={isInvoiceComplete} />
            <div className="p-4 space-y-4">
              {/* Check for two-invoice mode */}
              {Number(load.invoice_count) === 2 ? (
                <>
                  {/* Two Invoice Mode */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <Field label="Product" value={load.product} />
                    <Field label="Rate (₹/Kg)" value={load.rate ? formatRate(load.rate) : '-'} />
                    <Field label="Number of Invoices" value="2" />
                  </div>
                  
                  {/* Total Summary */}
                  <div className="p-3 bg-indigo-100 rounded-lg border border-indigo-300">
                    <p className="text-sm font-semibold text-indigo-700">
                      Total: {load.kgs || '0'} Kgs | {load.tons || '0'} Tons | {formatCurrency(load.invoice_amount || '0')}
                    </p>
                  </div>

                  {/* Invoice 1 */}
                  <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                    <div className="text-sm font-bold text-blue-800 mb-2 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">1</span>
                      Invoice 1
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <Field label="Kgs" value={load.kgs_1} />
                      <Field label="Tons" value={load.tons_1} />
                      <Field label="Invoice Number" value={load.invoice_number_1 || load.generated_invoice_number_1 || '-'} />
                      <Field label="Amount" value={formatCurrency(load.invoice_amount_1 || load.generated_invoice_amount_1)} />
                    </div>
                    {(load.invoice_filename_1 || load.generated_invoice_file_1) && (
                      <div className="flex items-center gap-2 p-2 mt-2 bg-white/50 rounded-lg">
                        <FileText className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-blue-700 flex-1">Invoice 1 File</span>
                        {onGetR2File && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="w-8 h-8 p-0 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg"
                            onClick={() => onGetR2File(load.load_id, 'invoice_1', pickDownloadName(load.invoice_filename_1, load.invoice_number_1 || load.generated_invoice_number_1, 'invoice_1'))}
                            disabled={attachmentLoading[`${load.load_id}_invoice_1`]}
                            title="Download"
                          >
                            {attachmentLoading[`${load.load_id}_invoice_1`] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Invoice 2 */}
                  <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
                    <div className="text-sm font-bold text-purple-800 mb-2 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs">2</span>
                      Invoice 2
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <Field label="Kgs" value={load.kgs_2} />
                      <Field label="Tons" value={load.tons_2} />
                      <Field label="Invoice Number" value={load.invoice_number_2 || load.generated_invoice_number_2 || '-'} />
                      <Field label="Amount" value={formatCurrency(load.invoice_amount_2 || load.generated_invoice_amount_2)} />
                    </div>
                    {(load.invoice_filename_2 || load.generated_invoice_file_2) && (
                      <div className="flex items-center gap-2 p-2 mt-2 bg-white/50 rounded-lg">
                        <FileText className="w-4 h-4 text-purple-600" />
                        <span className="text-sm text-purple-700 flex-1">Invoice 2 File</span>
                        {onGetR2File && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="w-8 h-8 p-0 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg"
                            onClick={() => onGetR2File(load.load_id, 'invoice_2', pickDownloadName(load.invoice_filename_2, load.invoice_number_2 || load.generated_invoice_number_2, 'invoice_2'))}
                            disabled={attachmentLoading[`${load.load_id}_invoice_2`]}
                            title="Download"
                          >
                            {attachmentLoading[`${load.load_id}_invoice_2`] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Single Invoice Mode */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Field label="Product" value={load.product} />
                    <Field label="Quantity (Kgs)" value={load.kgs} />
                    <Field label="Quantity (Tons)" value={load.tons} />
                    <Field label="Rate (₹/Kg)" value={load.rate ? formatRate(load.rate) : '-'} />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <Field label="Invoice Number" value={load.invoice_number || load.generated_invoice_number || '-'} />
                    <Field label="Invoice Amount" value={formatCurrency(load.invoice_amount || load.generated_invoice_amount)} />
                  </div>
                  
                  {/* Invoice File - handle both generated and manual uploads */}
                  {(load.invoice_filename || load.generated_invoice_file) && (
                    <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-700 flex-1">Invoice File</span>
                      <div className="flex items-center gap-1">
                        {/* Download button - always use R2 retrieval for reliable download */}
                        {onGetR2File && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="w-8 h-8 p-0 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg"
                            onClick={() => onGetR2File(load.load_id, 'invoice', pickDownloadName(load.invoice_filename, load.invoice_number || load.generated_invoice_number, 'invoice'))}
                            disabled={attachmentLoading[`${load.load_id}_invoice`]}
                            title="Download"
                          >
                            {attachmentLoading[`${load.load_id}_invoice`] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Permit File */}
                  {load.permit_filename && (
                    <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
                      <FileText className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700 flex-1">Permit File</span>
                      {onGetR2File && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="w-8 h-8 p-0 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg"
                          onClick={() => onGetR2File(load.load_id, 'permit', load.permit_filename)}
                          disabled={attachmentLoading[`${load.load_id}_permit`]}
                          title="Download"
                        >
                          {attachmentLoading[`${load.load_id}_permit`] ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Delivery & Status Section */}
          <div className={`border rounded-xl overflow-hidden ${isDeliveryComplete ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'}`}>
            <SectionHeader title="Delivery & Status" isComplete={isDeliveryComplete} />
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Delivery Status" value={load.delivery_status === 'delivered' ? 'Delivered' : 'In Progress'} />
                <Field label="Acceptance Status" value={load.acceptance_status?.charAt(0).toUpperCase() + load.acceptance_status?.slice(1)} />
                {load.rejection_reason && (
                  <Field label="Rejection Reason" value={load.rejection_reason} />
                )}
              </div>
            </div>
          </div>

          {/* Updated Invoice Section - BALAJI Only */}
          {isBalaji && load.acceptance_status === 'accepted' && (load.total_kgs_updated || load.updated_invoice_amount) && (
            <div className={`border rounded-xl overflow-hidden ${isUpdatedInvoiceComplete ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'}`}>
              <SectionHeader title="Updated Invoice" isComplete={isUpdatedInvoiceComplete} />
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Field label="Updated Kgs" value={load.total_kgs_updated} />
                  <Field label="Updated Tons" value={load.total_tons_updated || (load.total_kgs_updated ? (parseFloat(load.total_kgs_updated) / 1000).toFixed(3) : '-')} />
                  <Field label="Updated Invoice Number" value={load.updated_invoice_number || load.generated_updated_invoice_number || '-'} />
                  <Field label="Updated Amount" value={formatCurrency(load.updated_invoice_amount || load.generated_updated_invoice_amount)} />
                </div>
                {/* Updated Invoice File - handle both generated and manual uploads */}
                {(load.updated_invoice_file_url || load.generated_updated_invoice_file || load.updated_invoice_filename) && (
                  <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
                    <FileText className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-amber-700 flex-1">Updated Invoice File</span>
                    <div className="flex items-center gap-1">
                      {/* Download button - always use R2 retrieval for reliable download */}
                      {onGetR2File && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="w-8 h-8 p-0 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg"
                          onClick={() => onGetR2File(load.load_id, 'updated_invoice', pickDownloadName(load.updated_invoice_filename, load.updated_invoice_number || load.generated_updated_invoice_number, 'updated_invoice'))}
                          disabled={attachmentLoading[`${load.load_id}_updated_invoice`]}
                          title="Download"
                        >
                          {attachmentLoading[`${load.load_id}_updated_invoice`] ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Weight Adjustment Section - SVE Only */}
          {isSve && load.acceptance_status === 'accepted' && load.weight_status && (
            <div className={`border rounded-xl overflow-hidden ${isWeightAdjustmentComplete ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'}`}>
              <SectionHeader title="Weight Adjustment" isComplete={isWeightAdjustmentComplete} />
              <div className="p-4 space-y-4">
                <Field label="Weight Status" value={load.weight_status?.charAt(0).toUpperCase() + load.weight_status?.slice(1)} />
                
                {load.weight_status === 'addition' && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Field label="Additional Kgs" value={load.additional_kgs} />
                      <Field label="Additional Tons" value={load.additional_tons} />
                      <Field label="Addition Invoice Number" value={load.addition_invoice_number || load.generated_addition_invoice_number || '-'} />
                      <Field label="Additional Amount" value={formatCurrency(load.additional_invoice_amount)} />
                    </div>
                    {/* Addition Invoice File - handle both generated and manual uploads */}
                    {(load.generated_addition_invoice_file || load.addition_invoice_file_url || load.addition_invoice_filename) && (
                      <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg">
                        <FileText className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm text-emerald-700 flex-1">Addition Invoice File</span>
                        <div className="flex items-center gap-1">
                          {/* Download button - always use R2 retrieval for reliable download */}
                          {onGetR2File && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="w-8 h-8 p-0 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg"
                              onClick={() => onGetR2File(load.load_id, 'addition_invoice', pickDownloadName(load.addition_invoice_filename, load.addition_invoice_number || load.generated_addition_invoice_number, 'addition_invoice'))}
                              disabled={attachmentLoading[`${load.load_id}_addition_invoice`]}
                              title="Download"
                            >
                              {attachmentLoading[`${load.load_id}_addition_invoice`] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {load.weight_status === 'deduction' && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Field label="Deduction Kgs" value={load.deduction_kgs} />
                      <Field label="Deduction Tons" value={load.deduction_tons} />
                      <Field label="Deduction Invoice Number" value={load.deduction_invoice_number || load.generated_deduction_invoice_number || '-'} />
                      <Field label="Deduction Amount" value={formatCurrency(load.deduction_invoice_amount)} />
                    </div>
                    {/* Deduction Invoice File - handle both generated and manual uploads */}
                    {(load.generated_deduction_invoice_file || load.deduction_invoice_file_url || load.deduction_invoice_filename) && (
                      <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg">
                        <FileText className="w-4 h-4 text-red-600" />
                        <span className="text-sm text-red-700 flex-1">Deduction Invoice File</span>
                        <div className="flex items-center gap-1">
                          {onGetR2File && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="w-8 h-8 p-0 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg"
                              onClick={() => onGetR2File(load.load_id, 'deduction_invoice', pickDownloadName(load.deduction_invoice_filename, load.deduction_invoice_number || load.generated_deduction_invoice_number, 'deduction_invoice'))}
                              disabled={attachmentLoading[`${load.load_id}_deduction_invoice`]}
                              title="Download"
                            >
                              {attachmentLoading[`${load.load_id}_deduction_invoice`] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          </>
          )}

          {/* Billing and Follow-up Section */}
          {(load.bill_status || load.bill_payment_date || load.follow_up_email || load.follow_up_phone) && (
            <div className={`border rounded-xl overflow-hidden ${isBillingComplete ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'}`}>
              <SectionHeader title="Billing and Follow-up" isComplete={isBillingComplete} />
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Bill Status" value={load.bill_status?.charAt(0).toUpperCase() + load.bill_status?.slice(1)} />
                  <Field label="Payment Date" value={formatDate(load.bill_payment_date)} />
                  {/* Initial Due Days hidden from UI */}
                </div>
                {(load.follow_up_email || load.follow_up_phone) && (
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Follow-up Email" value={load.follow_up_email} />
                    <Field label="Follow-up Phone" value={load.follow_up_phone} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Tracking Section */}
          {(load.expected_payment_date || load.actual_payment_date) && (
            <div className={`border rounded-xl overflow-hidden ${isPaymentComplete ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'}`}>
              <SectionHeader title="Payment Tracking" isComplete={isPaymentComplete} />
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Field label="Expected Payment Date" value={formatDate(load.expected_payment_date)} />
                  <Field label="Actual Payment Date" value={formatDate(load.actual_payment_date)} />
                  {load.bill_status !== 'paid' && load.expected_payment_date && (
                    <div>
                      <Label className="text-xs text-slate-500">Days Remaining</Label>
                      <p className={`font-medium ${calculateDaysRemaining(load.expected_payment_date) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {calculateDaysRemaining(load.expected_payment_date)} days
                      </p>
                    </div>
                  )}
                  {load.bill_status === 'paid' && load.date_received && load.actual_payment_date && (
                    <div>
                      <Label className="text-xs text-slate-500">Paid In</Label>
                      <p className="font-medium text-green-600">
                        {Math.floor((new Date(load.actual_payment_date) - new Date(load.date_received)) / (1000 * 60 * 60 * 24))} days
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Other Bills Section */}
          {load.other_bills_filenames && load.other_bills_filenames.length > 0 && (
            <div className="border rounded-xl overflow-hidden bg-slate-50 border-slate-200">
              <SectionHeader title="Other Bills" isComplete={true} />
              <div className="p-4">
                <div className="flex flex-wrap gap-2">
                  {load.other_bills_filenames.map((filename, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-white/50 rounded-lg">
                      <FileText className="w-4 h-4 text-slate-600" />
                      <span className="text-sm text-slate-700">{filename}</span>
                      {onGetR2File && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => onGetR2File(load.load_id, 'other', filename, index)}
                          disabled={attachmentLoading[`${load.load_id}_other`]}
                        >
                          {attachmentLoading[`${load.load_id}_other`] ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Close Button */}
          <div className="flex justify-center pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => onClose(false)} 
              className="w-full max-w-xs h-11 rounded-xl"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
